import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '../../_models/user';
import { UserService } from '../../_services/user.service';
import { PageTitleService } from '../../_services/page-title.service';
import { PaymentMethodService } from '../../_services/payment-method.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';

declare var FlutterwaveCheckout: any;

interface Package {
    id: string;
    title: string;
    monthlyPrice: string;
    yearlyPrice: string;
    description: string;
    features: string[];
    selected?: boolean;
}

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.css'],
    standalone: false
})
export class SubscriptionComponent implements OnInit, OnDestroy {
    title = 'Subscription';
    titleKey = 'sidebar.subscription';
    loading = true;
    user: User;
    packages: Package[] = [];
    selectedPackage: Package | null = null;
    selectedPeriod: 'MONTHLY' | 'YEARLY' = 'MONTHLY';
    selectedPaymentMethod = 'FLUTTERWAVE';
    processing = false;
    showMoMoForm = false;
    moMoTxRef = '';
    moMoPhoneNumber = '';
    includeTablet = false;
    TABLET_PRICE = 200000;
    showConfirmModal = false;
    currentSubscription: any = null;
    loadingSubscription = true;
    premiumFeatures = [
        'List products on the platform',
        'Listed products on the home page',
        'Listed products on all our social platforms',
        'Have one-to-one chats with our verified agropastoral technicians',
        'Training'
    ];

    private subscriptions: Subscription[] = [];

    constructor(
        public titleService: Title,
        private pageTitleService: PageTitleService,
        private userService: UserService,
        private paymentMethodService: PaymentMethodService,
        private router: Router,
        private translateService: TranslateService,
        private route: ActivatedRoute,
        private toastr: ToastrService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        if (this.user) {
            this.pageTitleService.setPageTitle(this.title);
            titleService.setTitle(this.title);

            const subs = this.translateService.get(this.titleKey).subscribe(value => {
                this.titleService.setTitle(value);
            });
            this.subscriptions.push(subs);
        }
    }

    ngOnInit() {
        this.loadPackages();
        this.loadUserSubscription();
    }

    loadUserSubscription() {
        this.userService.getUserSubscription().subscribe({
            next: (response: any) => {
                this.currentSubscription = response;
                this.loadingSubscription = false;
            },
            error: (error) => {
                this.loadingSubscription = false;
            }
        });
    }

    loadPackages() {
        this.userService.getPackages().subscribe({
            next: (response: any) => {
                const rawPackages = response.data || [];
                this.packages = rawPackages.map((pkg: any) => ({
                    ...pkg,
                    features: this.parseDescriptionFeatures(pkg.description)
                }));

                // Default select FREE package
                const freePackage = this.packages.find(p => p.title === 'FREE');
                if (freePackage) {
                    freePackage.selected = true;
                    this.selectedPackage = freePackage;
                }

                this.loading = false;
            },
            error: (error) => {
                this.toastr.error('Failed to load subscription packages');
                this.loading = false;
            }
        });
    }

    parseDescriptionFeatures(description: string): string[] {
        if (!description) {
            return [];
        }

        const features: string[] = [];
        const liRegex = /<li>(.*?)<\/li>/gi;
        let match;
        while ((match = liRegex.exec(description)) !== null) {
            const cleanFeature = match[1].replace(/<[^>]*>/g, '').trim();
            if (cleanFeature) {
                features.push(cleanFeature);
            }
        }

        if (features.length === 0) {
            const plainText = description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            if (plainText) {
                return [plainText];
            }
        }

        return features;
    }

    selectPackage(pkg: Package) {
        this.packages.forEach(p => p.selected = false);
        pkg.selected = true;
        this.selectedPackage = pkg;
    }

    getFormattedPrice(price: number | string): string {
        const num = typeof price === 'string' ? parseFloat(price) : price;
        if (num === 0) {
            return 'Free';
        }
        return num.toLocaleString() + ' XAF';
    }

    getIsFreePackage(pkg: Package): boolean {
        return pkg.title === 'FREE';
    }

    getYearlySavings(pkg: Package): number {
        if (!pkg || parseFloat(pkg.monthlyPrice) === 0) {
            return 0;
        }
        const monthlyTotal = parseFloat(pkg.monthlyPrice) * 12;
        const yearlyTotal = parseFloat(pkg.yearlyPrice);
        return Math.max(0, monthlyTotal - yearlyTotal);
    }

    getBasePrice(pkg: Package): number {
        if (!pkg) return 0;
        return parseFloat(this.selectedPeriod === 'MONTHLY' ? pkg.monthlyPrice : pkg.yearlyPrice);
    }

    getCurrentPrice(pkg: Package): number {
        if (!pkg) {
            return 0;
        }
        const basePrice = parseFloat(this.selectedPeriod === 'MONTHLY' ? pkg.monthlyPrice : pkg.yearlyPrice);
        return basePrice + (this.includeTablet ? this.TABLET_PRICE : 0);
    }

    getSubscriptionPrice(pkg: Package): number {
        if (!pkg) return 0;
        return parseFloat(this.selectedPeriod === 'MONTHLY' ? pkg.monthlyPrice : pkg.yearlyPrice);
    }

    openConfirmModal(): void {
        if (!this.selectedPackage || this.getIsFreePackage(this.selectedPackage)) {
            return;
        }
        this.showConfirmModal = true;
    }

    closeConfirmModal(): void {
        this.showConfirmModal = false;
    }

    get hasActivePremium(): boolean {
        return this.currentSubscription?.data?.status === 'ACTIVE' && 
               this.currentSubscription?.data?.packageTitle === 'PREMIUM';
    }

    // eFarm Mobile Money payments come back as payment.status = CONFIRMED
    // (subscription INACTIVE) until an admin approves them
    get paymentConfirmed(): boolean {
        const p = this.subscriptionData?.payment;
        return p?.paymentMethod === 'COMPANY_MOBILE_MONEY' && p?.status === 'CONFIRMED';
    }

    get subscriptionData(): any {
        return this.currentSubscription?.data || null;
    }

    get daysRemaining(): number {
        if (!this.subscriptionData?.endDate) return 0;
        const end = new Date(this.subscriptionData.endDate);
        const now = new Date();
        const diff = end.getTime() - now.getTime();
        return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
    }

    get totalDays(): number {
        if (!this.subscriptionData?.startDate || !this.subscriptionData?.endDate) return 0;
        const start = new Date(this.subscriptionData.startDate);
        const end = new Date(this.subscriptionData.endDate);
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
    }

    get daysUsed(): number {
        if (!this.subscriptionData?.startDate) return 0;
        const start = new Date(this.subscriptionData.startDate);
        const now = new Date();
        return Math.ceil((now.getTime() - start.getTime()) / (1000 * 3600 * 24));
    }

    get progressPercent(): number {
        if (this.totalDays === 0) return 0;
        return Math.min(100, Math.max(0, (this.daysUsed / this.totalDays) * 100));
    }

    formatDate(dateString: string): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    getSellerInitials(): string {
        const companyName = this.subscriptionData?.seller?.companyName || '';
        if (!companyName) return '';
        const words = companyName.trim().split(/\s+/);
        if (words.length === 1) return words[0].charAt(0).toUpperCase();
        return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    }

    getSellerLocation(): string {
        const city = this.subscriptionData?.seller?.operationalInfo?.city || '';
        const country = this.subscriptionData?.seller?.operationalInfo?.country || '';
        if (city && country) return `${city}, ${country}`;
        return city || country || '';
    }

    purchase() {
        this.closeConfirmModal();
        
        if (!this.selectedPackage || this.getIsFreePackage(this.selectedPackage)) {
            return;
        }

        // For Company Mobile Money: show modal first to collect phone + transactionId
        if (this.selectedPaymentMethod === 'COMPANY_MOBILE_MONEY') {
            this.showMoMoForm = true;
            return;
        }

        // For Flutterwave: proceed with payment initialization directly
        this.processing = true;

        const payload = {
            paymentType: 'SUBSCRIPTION',
            referenceId: this.selectedPackage.id,
            amount: this.getCurrentPrice(this.selectedPackage),
            paymentMethod: this.selectedPaymentMethod,
            subscriptionPeriod: this.selectedPeriod,
            tablet: this.includeTablet
        };

        this.paymentMethodService.initSubscriptionPayment(payload).subscribe({
            next: (response: any) => {
                this.handleFlutterwavePayment(response);
            },
            error: (error) => {
                this.processing = false;
                this.toastr.error('Payment initialization failed. Please try again.');
            }
        });
    }

    handleFlutterwavePayment(response: any) {
        const txRef = response.data?.transactionRef || response.tx_ref || response.transaction_id;
        const amount = this.getCurrentPrice(this.selectedPackage);

        FlutterwaveCheckout({
            public_key: environment.flutterwaveKey,
            tx_ref: txRef,
            amount: amount,
            currency: 'XAF',
            payment_options: 'mobilemoneyfranco,ussd,card',
            customer: {
                name: this.user.firstName + ' ' + this.user.lastName,
                email: this.user.email || '',
                phone_number: this.user.phone || ''
            },
            callback: (paymentResponse: any) => {
                this.confirmPayment(paymentResponse, txRef);
            },
            onclose: () => {
                this.processing = false;
            }
        });
    }

    confirmPayment(paymentResponse: any, txRef: string) {
        const confirmPayload = {
            status: paymentResponse.status || 'success',
            transaction_id: paymentResponse.transaction_id || '',
            tx_ref: txRef,
            flw_ref: paymentResponse.flw_ref || '',
            currency: paymentResponse.currency || 'XAF',
            amount: this.getCurrentPrice(this.selectedPackage),
            customer: {
                name: this.user.firstName + ' ' + this.user.lastName,
                email: this.user.email || '',
                phone_number: this.user.phone || ''
            }
        };

        this.paymentMethodService.confirmSubscriptionPayment(confirmPayload).subscribe({
            next: () => {
                this.processing = false;
                sessionStorage.setItem('payment_success', 'Your payment has been successfully done');
                window.location.href = '/user/dashboard';
            },
            error: (error) => {
                this.processing = false;
                this.toastr.error('Payment confirmation failed. Please contact support.');
            }
        });
    }

    submitMoMoPayment() {
        if (!this.moMoTxRef || !this.moMoPhoneNumber) {
            this.toastr.error('Please fill in all mobile money details');
            return;
        }

        this.processing = true;

        const payload = {
            paymentType: 'SUBSCRIPTION',
            referenceId: this.selectedPackage!.id,
            amount: this.getCurrentPrice(this.selectedPackage!),
            paymentMethod: 'COMPANY_MOBILE_MONEY',
            phoneNumber: this.moMoPhoneNumber,
            mobileMoneyTransactionId: this.moMoTxRef,
            subscriptionPeriod: this.selectedPeriod,
            tablet: this.includeTablet
        };

        this.paymentMethodService.initSubscriptionPayment(payload).subscribe({
            next: (response: any) => {
                const txRef = response.data?.transactionRef || response.tx_ref || response.transaction_id;

                const confirmPayload = {
                    status: 'success',
                    transaction_id: this.moMoTxRef,
                    tx_ref: txRef || this.moMoTxRef,
                    flw_ref: '',
                    currency: 'XAF',
                    amount: this.getCurrentPrice(this.selectedPackage!),
                    customer: {
                        name: this.user.firstName + ' ' + this.user.lastName,
                        email: this.user.email || '',
                        phone_number: this.moMoPhoneNumber
                    }
                };

                this.paymentMethodService.confirmSubscriptionPayment(confirmPayload).subscribe({
                    next: () => {
                        this.processing = false;
                        this.showMoMoForm = false;
                        // Show subscription details + "payment confirmed" notice on this page
                        this.currentSubscription = response;
                        this.loadUserSubscription();
                    },
                    error: (error) => {
                        // The init-payment response already came back with payment.status = CONFIRMED,
                        // so a failing confirm call must not block the seller
                        this.processing = false;
                        this.showMoMoForm = false;
                        this.currentSubscription = response;
                        this.loadUserSubscription();
                    }
                });
            },
            error: (error) => {
                this.processing = false;
                this.toastr.error('Payment initialization failed. Please try again.');
            }
        });
    }

    cancelMoMoPayment() {
        this.showMoMoForm = false;
        this.processing = false;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
